// Exposure contract: docs/analytics/experiments.md
const EXPOSABLE_VARIANTS = ['control', 'test'];

// Module lifetime is the pageview: Alpine components re-initialize (for
// example on every search) but each experiment is exposed once.
const exposed = new Set();

/**
 * Captures `experiment_exposed` once per experiment per pageview.
 *
 * @param {string|null|undefined} experimentKey LaunchDarkly flag key, also the PostHog experiment key.
 * @param {string|null|undefined} variant Assigned group; anything but control/test is ignored.
 * @param {Object<string, *>|null|undefined} properties Extra event properties; anything but a plain object is ignored.
 * @returns {boolean} True when this call handed the event to posthog.
 */
export function captureExperimentExposure(experimentKey, variant, properties) {
    if (typeof experimentKey !== 'string' || experimentKey === '') return false;
    if (!EXPOSABLE_VARIANTS.includes(variant)) return false;
    if (exposed.has(experimentKey)) return false;

    // posthog loads after analytics consent: stay unlatched so a later call retries.
    if (typeof window.posthog?.capture !== 'function') return false;

    try {
        const extra = properties !== null && typeof properties === 'object' && !Array.isArray(properties) ? properties : {};
        // the key and group go last so no extra property can relabel the exposure
        window.posthog.capture('experiment_exposed', {
            ...extra,
            experiment_key: experimentKey,
            ['$feature/' + experimentKey]: variant,
        });
        exposed.add(experimentKey);

        return true;
    } catch (err) {
        if (window.Sentry && typeof window.Sentry.captureException === 'function') {
            window.Sentry.captureException(err);
        } else {
            console.warn('Experiment exposure capture failed:', err);
        }

        return false;
    }
}
